import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load cart from localStorage when user logs in
  useEffect(() => {
    if (user && token) {
      const savedCart = localStorage.getItem(`cart_${user.user_id || user.email}`);
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (e) {
          console.error('Failed to parse saved cart:', e);
          setCartItems([]);
        }
      }
    } else {
      setCartItems([]);
    }
  }, [user, token]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (user && token && cartItems.length >= 0) {
      localStorage.setItem(`cart_${user.user_id || user.email}`, JSON.stringify(cartItems));
    }
  }, [cartItems, user, token]);

  // Add item to cart
  const addToCart = useCallback((item) => {
    if (!user || !token) {
      setNotification({
        type: 'error',
        message: 'Please log in to add items to your cart',
        item: null
      });
      setTimeout(() => setNotification(null), 3000);
      return false;
    }

    setCartItems(prev => {
      const existingIndex = prev.findIndex(
        i => (i.item_id || i.equipment_id) === (item.item_id || item.equipment_id)
      );
      
      if (existingIndex >= 0) {
        // Update quantity if item exists
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: (updated[existingIndex].quantity || 1) + 1
        };
        return updated;
      }
      
      // Add new item
      return [...prev, { ...item, quantity: 1 }];
    });

    setNotification({
      type: 'success',
      message: 'Added to cart!',
      item: item
    });
    
    setTimeout(() => setNotification(null), 3000);
    return true;
  }, [user, token]);

  // Remove item from cart
  const removeFromCart = useCallback((itemId) => {
    setCartItems(prev => prev.filter(
      item => (item.item_id || item.equipment_id) !== itemId
    ));
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCartItems(prev => prev.map(item => {
      if ((item.item_id || item.equipment_id) === itemId) {
        return { ...item, quantity };
      }
      return item;
    }));
  }, [removeFromCart]);

  // Clear cart
  const clearCart = useCallback(() => {
    setCartItems([]);
    if (user) {
      localStorage.removeItem(`cart_${user.user_id || user.email}`);
    }
  }, [user]);

  // Get total items count
  const getItemCount = useCallback(() => {
    return cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }, [cartItems]);

  // Get total price
  const getTotalPrice = useCallback(() => {
    return cartItems.reduce((sum, item) => {
      const price = item.price || item.price_per_hour || 0;
      return sum + (price * (item.quantity || 1));
    }, 0);
  }, [cartItems]);

  // Open cart modal
  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  // Close cart modal
  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Toggle cart modal
  const toggleCart = useCallback(() => {
    setIsCartOpen(prev => !prev);
  }, []);

  const value = {
    cartItems,
    isCartOpen,
    notification,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemCount,
    getTotalPrice,
    openCart,
    closeCart,
    toggleCart,
    isLoggedIn: !!(user && token)
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
